import type { MatrixAppColumn, MatrixRoleRow } from '../RoleCoverage/coverageMatrixHelpers';

/**
 * EAA-1817 (design frame 55c): the two export scopes the SERVER CANNOT PRODUCE.
 *
 * `GET /organizations/{id}/role-map/export` walks that org's `RoleMap` rows. Two
 * of the frame's four scopes are not sets of rows:
 *
 *   - **"Chỉ ô chưa phủ"** — a standard role × application pair with NO link.
 *     There is no row to export; asking the server for it would be asking it to
 *     invent one per absent pair.
 *   - **"Chỉ mục cần xử lý"** — the paused pairs. These DO have rows, but the
 *     endpoint takes `ids` and the console knows which ids are paused only
 *     because it already holds the coverage grid.
 *
 * Both are pure derivations of data the Ma trận view has already loaded, so they
 * are generated here. That is not a workaround for a missing endpoint — the
 * absent-pair set genuinely does not exist as a server resource.
 *
 * The two SERVER scopes ("app đang chọn", "toàn bộ") still go through the real
 * endpoint: those are rows, they are org-scoped, and re-deriving them client-side
 * would give a second, drifting definition of the same file.
 */

/** Which slice of the grid a client-side export covers. */
export type ClientCsvScope = 'uncovered' | 'needsAttention';

export interface ClientCsvOptions {
    scope: ClientCsvScope;
    rows: MatrixRoleRow[];
    columns: MatrixAppColumn[];
    /** Include the app-role KEY column, mirroring the server's `include_codes`. */
    includeCodes: boolean;
    /** Column headers, localised by the caller. */
    headers: {
        roleKey: string;
        roleName: string;
        application: string;
        appName: string;
        appRole: string;
        appRoleKey: string;
        status: string;
    };
    /** Localised cell values for the status column. */
    statusLabels: { uncovered: string; paused: string };
}

/** RFC 4180 quoting: wrap in quotes and double any embedded quote. Applied to
 *  every field rather than only the ones that look risky — a role name is
 *  free text and Vietnamese copy contains commas often enough. */
function quote(value: string | number): string {
    return `"${String(value).replace(/"/g, '""')}"`;
}

/**
 * Build the CSV text for a client-derived scope.
 *
 * Not-ready applications are skipped entirely: their cells render as "—" in the
 * grid because nothing can be linked into them yet, so listing them as
 * "uncovered" would report work that cannot be done.
 */
export function buildClientRoleMapCsv(options: ClientCsvOptions): string {
    const { scope, rows, columns, includeCodes, headers, statusLabels } = options;

    const header = [
        headers.roleKey,
        headers.roleName,
        headers.application,
        headers.appName,
        headers.appRole,
        ...(includeCodes ? [headers.appRoleKey] : []),
        headers.status,
    ];

    const lines: string[] = [header.map(quote).join(',')];

    for (const row of rows) {
        for (let i = 0; i < row.cells.length; i += 1) {
            const column = columns[i];
            if (!column || !column.ready) continue;
            const cell = row.cells[i];
            if (scope === 'uncovered' && cell.state !== 'none') continue;
            if (scope === 'needsAttention' && cell.state !== 'paused') continue;

            const record = [
                row.roleKey,
                row.roleName,
                column.code,
                column.name,
                // An uncovered pair has no app role at all; a paused one still
                // names the role the app dropped, which is the whole point of
                // exporting it.
                cell.roleNames.join('; '),
                ...(includeCodes ? [cell.roles.join('; ')] : []),
                cell.state === 'none' ? statusLabels.uncovered : statusLabels.paused,
            ];
            lines.push(record.map(quote).join(','));
        }
    }

    return lines.join('\r\n');
}

/** How many rows a client-derived scope would produce — the count the frame
 *  prints beside each option, so it must come from the same predicate the
 *  builder uses rather than a parallel tally. */
export function countClientCsvRows(
    scope: ClientCsvScope,
    rows: MatrixRoleRow[],
    columns: MatrixAppColumn[]
): number {
    let total = 0;
    for (const row of rows) {
        for (let i = 0; i < row.cells.length; i += 1) {
            if (!columns[i]?.ready) continue;
            const state = row.cells[i].state;
            if (scope === 'uncovered' ? state === 'none' : state === 'paused') total += 1;
        }
    }
    return total;
}

/**
 * Hand the browser a generated file.
 *
 * The BOM is not decoration: Excel on Windows reads a UTF-8 CSV as the local
 * codepage without it, which turns every Vietnamese role name into mojibake —
 * and this file exists to be opened in Excel.
 */
export function downloadCsvText(filename: string, text: string): void {
    // `\uFEFF` as an escape, not a literal BOM: a literal one is invisible in
    // the source and lint flags it as irregular whitespace.
    const blob = new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}
